import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Coffee, MapPin, Phone, Clock, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3003';
const API = `${BACKEND_URL}/api`;

const MenuPage = () => {
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [activeCategory, setActiveCategory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isOpen, setIsOpen] = useState(true);

    useEffect(() => {
        fetchMenuData();
        checkOpenStatus();
    }, []);

    const fetchMenuData = async () => {
        try {
            const res = await fetch(`${API}/menu`);
            const data = await res.json();
            setCategories(data.categories || []);
            setProducts(data.products || []);
            if (data.categories?.length > 0) {
                setActiveCategory(data.categories[0].id);
            }
        } catch (e) {
            console.error(e);
        }
        setLoading(false);
    };

    const checkOpenStatus = () => {
        const now = new Date();
        const hours = now.getHours();
        setIsOpen(hours >= 8 && hours <= 23);
    };

    const filteredProducts = activeCategory
        ? products.filter(p => p.category_id === activeCategory)
        : products;

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0C0A09] flex items-center justify-center">
                <div className="animate-spin w-8 h-8 border-2 border-[#D4A373] border-t-transparent rounded-full" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0C0A09]" data-testid="menu-page">
            {/* Header */}
            <header className="bg-[#0C0A09]/80 backdrop-blur-md border-b border-[#292524] sticky top-0 z-50">
                <div className="max-w-4xl mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <Link to="/" className="flex items-center gap-3">
                            <img src="/rott_logo_new_v2.jpg" alt="Rott Coffee Logo" className="w-8 h-8 rounded-full border border-[#D4A373]/30" />
                            <span className="font-heading font-bold text-[#F5F5F4] text-xl">
                                Rott <span className="text-[#D4A373]">Coffee Co</span>
                            </span>
                        </Link>
                        <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${isOpen ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
                            <span className={`text-xs ${isOpen ? 'text-green-500' : 'text-red-500'}`}>
                                {isOpen ? 'Açık' : 'Kapalı'}
                            </span>
                        </div>
                    </div>
                </div>
            </header>

            {/* Hero Banner */}
            <div className="relative h-48 md:h-64 overflow-hidden border-b border-[#292524]">
                <img
                    src="https://images.unsplash.com/photo-1738597966728-349bf6247848?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
                    alt="Rott Coffee Co Menu"
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0C0A09] via-[#0C0A09]/50 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="max-w-4xl mx-auto">
                        <h1 className="font-heading font-bold text-3xl md:text-5xl text-[#F5F5F4] mb-2 tracking-tight">
                            Menümüz
                        </h1>
                        <div className="flex items-center gap-4 text-sm">
                            <div className="flex items-center gap-1">
                                {[...Array(5)].map((_, i) => (
                                    <Star key={i} className="w-4 h-4 fill-[#D4A373] text-[#D4A373]" />
                                ))}
                                <span className="text-[#F5F5F4] ml-1">4.9</span>
                            </div>
                            <span className="text-[#57534E]">•</span>
                            <span className="text-[#A8A29E]">Ayrancı, Ankara</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Category Tabs */}
            {categories.length > 0 && (
                <div className="sticky top-[60px] z-40 bg-[#0C0A09] border-b border-[#292524]">
                    <div className="max-w-4xl mx-auto px-4">
                        <div className="flex overflow-x-auto scrollbar-hide gap-3 py-4 -mx-4 px-4">
                            {categories.map((category) => (
                                <button
                                    key={category.id}
                                    onClick={() => setActiveCategory(category.id)}
                                    className={`px-5 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-all ${activeCategory === category.id
                                        ? 'bg-[#D4A373] text-[#0C0A09]'
                                        : 'bg-[#1C1917] text-[#A8A29E] border border-[#292524] hover:bg-[#292524]'
                                        }`}
                                    data-testid={`category-tab-${category.id}`}
                                >
                                    {category.name}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Products */}
            <div className="max-w-4xl mx-auto px-4 py-8">
                {categories.length === 0 || products.length === 0 ? (
                    <div className="text-center py-16">
                        <Coffee className="w-16 h-16 text-[#292524] mx-auto mb-4" />
                        <p className="text-[#A8A29E] text-lg">Menü hazırlanıyor...</p>
                        <p className="text-[#57534E] text-sm mt-2">Yakında burada lezzetli ürünlerimizi göreceksiniz.</p>
                    </div>
                ) : filteredProducts.length === 0 ? (
                    <div className="text-center py-16">
                        <Coffee className="w-16 h-16 text-[#292524] mx-auto mb-4" />
                        <p className="text-[#A8A29E] text-lg">Bu kategoride ürün bulunmuyor.</p>
                    </div>
                ) : (
                    <motion.div
                        key={activeCategory}
                        className="grid gap-6 md:grid-cols-2"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                    >
                        {filteredProducts.map((product, index) => (
                            <motion.div
                                key={product.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: index * 0.05 }}
                                className="glass-card bg-[#1C1917] border border-[#292524] rounded-2xl overflow-hidden flex hover:border-[#D4A373]/30 transition-colors"
                                data-testid={`product-card-${product.id}`}
                            >

                                {/* Content */}
                                <div className="flex-1 p-4 flex flex-col justify-between min-w-0">
                                    <div>
                                        <h3 className="font-heading font-semibold text-lg text-[#F5F5F4] truncate">
                                            {product.name}
                                        </h3>
                                    </div>
                                    <div className="mt-3">
                                        <span className="text-[#D4A373] font-bold text-lg">
                                            ₺{product.price.toFixed(2)}
                                        </span>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                )}
            </div>

            {/* Footer Info */}
            <div className="bg-[#1C1917] mt-12 border-t border-[#292524]">
                <div className="max-w-4xl mx-auto px-4 py-8">
                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-full bg-[#D4A373]/10 flex items-center justify-center flex-shrink-0">
                                <MapPin className="w-5 h-5 text-[#D4A373]" />
                            </div>
                            <div>
                                <p className="text-[#F5F5F4] font-medium text-sm mb-1">Adres</p>
                                <p className="text-[#A8A29E] text-sm">
                                    Şht. Mahir Turan Sk. No:8<br />
                                    06540 Çankaya / Ankara
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-full bg-[#D4A373]/10 flex items-center justify-center flex-shrink-0">
                                <Phone className="w-5 h-5 text-[#D4A373]" />
                            </div>
                            <div>
                                <p className="text-[#F5F5F4] font-medium text-sm mb-1">Telefon</p>
                                <a href="tel:+905458761969" className="text-[#A8A29E] text-sm hover:text-[#D4A373] transition-colors">
                                    0545 876 19 69
                                </a>
                            </div>
                        </div>

                        <div className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-full bg-[#D4A373]/10 flex items-center justify-center flex-shrink-0">
                                <Clock className="w-5 h-5 text-[#D4A373]" />
                            </div>
                            <div>
                                <p className="text-[#F5F5F4] font-medium text-sm mb-1">Çalışma Saatleri</p>
                                <p className="text-[#A8A29E] text-sm">Her gün 08:00 - 23:30</p>
                            </div>
                        </div>
                    </div>

                    <div className="mt-10 pt-6 border-t border-[#292524] text-center">
                        <Link to="/" className="text-[#57534E] text-sm hover:text-[#D4A373] transition-colors inline-block pb-4">
                            ← Ana Sayfaya Dön
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MenuPage;
